/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Navbar, Nav, Badge, Accordion, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEO/SEOHead';
import { MultipleStructuredData } from '../components/SEO/StructuredData';
import LanguageSelector from '../components/LanguageSelector';
import { getSafeTranslation } from '../utils/i18nSafety';
import { getFeaturedGuides } from '../guides/featuredGuides';

const FAQ_SCHEMA_KEYS = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9'];

const stripHtml = (value) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const LandingPage = () => {
  const { t, ready } = useTranslation(['landing', 'guides', 'common']);
  const [isReady, setIsReady] = useState(false);

  // Wait for i18n to be ready
  useEffect(() => {
    if (ready) {
      setIsReady(true);
    }
  }, [ready]);

  // Safe translation helper
  const getSafeArray = (key, fallback = []) => {
    return getSafeTranslation(t, key, fallback);
  };

  // Show loading state while i18n is loading
  if (!isReady) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="text-muted">Chargement...</p>
        </div>
      </div>
    );
  }
  const featuredGuides = getFeaturedGuides();

  // Configuration SEO
  const seoConfig = {
    title: t('seo.title'),
    description: t('seo.description'),
    keywords: t('seo.keywords'),
    canonical: '/',
    ogType: 'website'
  };

  const faqItemsForSchema = FAQ_SCHEMA_KEYS.map((key) => ({
    question: t(`faq.${key}.question`),
    answer: stripHtml(t(`faq.${key}.answer`)),
  })).filter((item) => item.question && item.answer);

  // Configuration des structured data
  const structuredDataSchemas = [
    { type: 'organization' },
    { type: 'softwareApplication' },
    { type: 'website' },
    {
      type: 'faqPage',
      data: {
        faqItems: faqItemsForSchema,
      },
    },
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead {...seoConfig} />
      
      {/* Structured Data JSON-LD */}
      <MultipleStructuredData schemas={structuredDataSchemas} />

      <header>
        <Navbar expand="lg" className="navbar-landing shadow-sm">
          <Container>
            {/* Brand */}
            <Navbar.Brand href="/" className="navbar-brand-landing">
              <div className="landing-brand-icon">
                <i className="bi bi-headset"></i>
              </div>
              <div className="landing-brand-text">
                <span className="d-none d-sm-inline">{t('brand.name')}</span>
                <span className="d-inline d-sm-none">{t('brand.shortName')}</span>
              </div>
            </Navbar.Brand>
            
            <Navbar.Toggle aria-controls="landing-navbar-nav">
              <span className="navbar-toggler-icon"></span>
            </Navbar.Toggle>
            
            <Navbar.Collapse id="landing-navbar-nav">
              {/* Navigation Links */}
              <Nav className="navbar-nav-landing mx-auto">
                <Nav.Link href="#features" className="nav-link-landing">
                  <i className="bi bi-stars me-2 d-lg-none"></i>
                  {t('common:footer.features')}
                </Nav.Link>
                <Nav.Link href="#use-cases" className="nav-link-landing">
                  <i className="bi bi-briefcase me-2 d-lg-none"></i>
                  {t('useCases.title')}
                </Nav.Link>
                <Nav.Link href="#how-it-works" className="nav-link-landing">
                  <i className="bi bi-gear me-2 d-lg-none"></i>
                  {t('nav.howItWorks')}
                </Nav.Link>
                <Nav.Link as={Link} to="/guides" className="nav-link-landing">
                  <i className="bi bi-journal-text me-2 d-lg-none"></i>
                  {t('nav.guides')}
                </Nav.Link>
                <Nav.Link href="#faq" className="nav-link-landing">
                  <i className="bi bi-question-circle me-2 d-lg-none"></i>
                  FAQ
                </Nav.Link>
              </Nav>
              
              {/* Actions */}
              <Nav className="navbar-actions-landing">
                <div className="d-flex flex-column flex-lg-row align-items-stretch align-items-lg-center gap-2">
                  <LanguageSelector variant="outline-primary" size="sm" className="landing-lang-selector" />
                  <Nav.Link as={Link} to="/login" className="nav-link-login">
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    {t('common:nav.login')}
                  </Nav.Link>
                  <Nav.Link as={Link} to="/register" className="nav-link-register">
                    <Button variant="primary" className="btn-register-landing">
                      <i className="bi bi-rocket-takeoff me-2"></i>
                      {t('hero.cta.primary')}
                    </Button>
                  </Nav.Link>
                </div>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
      </header>

      <main>
      {/* Hero Section */}
      <section className="hero-section" aria-label="Hero section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="hero-content text-center text-lg-start">
            <Badge bg="light" text="primary" className="mb-3 px-2 px-md-3 py-2 rounded-pill border fw-bold landing-text-xs">
              <i className="bi bi-gift-fill me-1 text-warning"></i> {t('hero.badge')}
            </Badge>
            <h1 className="display-5 display-md-4 fw-bold mb-3 mb-md-4 px-2 px-md-0">
              {t('hero.title')},<br className="d-none d-md-block" />
              <span className="text-white opacity-75">{t('hero.subtitle')}</span>
            </h1>
            <p className="lead mb-3 mb-md-4 text-white opacity-90 px-3 px-md-0 landing-text">
              {t('hero.description')}
            </p>
            
            {/* Message gratuité mis en avant */}
            <Alert variant="light" className="mb-3 mb-md-4 mx-2 mx-md-0 border-0 shadow-sm" style={{ backgroundColor: 'rgba(255, 255, 255, 0.95)' }}>
              <div className="d-flex align-items-start">
                <div className="me-2 me-md-3">
                  <i className="bi bi-shield-check-fill text-success" style={{ fontSize: '1.5rem' }}></i>
                </div>
                <div className="text-start">
                  <h5 className="fw-bold mb-2 text-dark landing-text">
                    <i className="bi bi-lightning-charge-fill text-warning me-2"></i>
                    {t('hero.alertTitle')}
                  </h5>
                  <p className="mb-0 text-muted landing-text-sm" style={{ lineHeight: '1.5' }}>
                    {t('hero.alertDescription')}
                  </p>
                </div>
              </div>
            </Alert>
            
            <div className="mb-3 mb-md-4 mx-2 mx-md-0 p-3 bg-white bg-opacity-10 rounded-3">
              <p className="mb-2 fw-semibold text-white landing-text-md">
                <i className="bi bi-lightning-charge-fill text-warning me-2"></i>
                {t('hero.howItWorks')}
              </p>
              <ol className="text-white opacity-90 mb-0 ps-3 landing-text-sm" style={{ lineHeight: '1.7' }}>
                {getSafeArray('hero.steps', []).map((step, index) => (
                  <li key={index} className={index < 3 ? "mb-2" : ""} dangerouslySetInnerHTML={{ __html: step }} />
                ))}
              </ol>
            </div>
            
              <div className="d-flex flex-column flex-sm-row gap-2 gap-md-3 justify-content-center justify-content-lg-start mb-3 mb-md-4 px-2 px-md-0">
              <Button as={Link} to="/register" className="btn-hero shadow-lg" size="lg">
                <i className="bi bi-lightning-charge-fill me-2"></i>
                {t('hero.cta.primary')}
                <i className="bi bi-arrow-right ms-2"></i>
              </Button>
            </div>

              <div className="d-flex flex-column flex-sm-row flex-wrap justify-content-center justify-content-lg-start gap-2 gap-md-3 text-white opacity-90 px-2 px-md-0 landing-text-sm">
              {getSafeArray('hero.benefits', []).map((benefit, index) => (
                <div key={index} className="d-flex align-items-center">
                  <i className="bi bi-check-circle-fill me-2 text-success"></i> 
                  <span dangerouslySetInnerHTML={{ __html: benefit }} />
                </div>
              ))}
            </div>
            </Col>
            <Col lg={6} className="d-none d-lg-block">
              <div className="hero-image-container">
                <img 
                  src="/images/hero-illustration.svg" 
                  alt={t('hero.imageAlt')}
                  className="img-fluid hero-illustration"
                  loading="eager"
                  decoding="async"
                />
          </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* KPI Section */}
      <section className="section bg-white py-4 py-md-5 border-bottom" aria-label="Key performance indicators">
        <Container>
          <Row className="text-center g-3 g-md-4">
            {getSafeArray('kpi.items', []).map((kpi, index) => {
              const icons = ['bi-graph-up-arrow', 'bi-truck', 'bi-star-fill', 'bi-lightning-charge-fill'];
              const colors = ['primary', 'success', 'warning', 'info'];
              return (
                <Col key={index} xs={6} md={3} className="kpi-item">
                  <div className="kpi-icon-wrapper mb-2 mb-md-3">
                    <div className={`kpi-icon bg-${colors[index]} bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center`} style={{ width: '60px', height: '60px' }}>
                      <i className={`bi ${icons[index]} text-${colors[index]} fs-4`}></i>
                    </div>
                  </div>
                  <h3 className={`fw-bold text-${colors[index]} mb-1 mb-md-2`} dangerouslySetInnerHTML={{ __html: kpi.value }} />
                  <p className="text-muted mb-0 landing-text-sm">{kpi.description}</p>
                </Col>
              );
            })}
          </Row>
          {t('kpi.footnote') && (
            <p className="text-muted text-center mt-3 mb-0 landing-text-xs">{t('kpi.footnote')}</p>
          )}
        </Container>
      </section>

      {/* How it works */}
      <section className="section py-4 py-md-5" id="how-it-works" aria-label="How it works">
        <Container>
          <div className="text-center mb-4 mb-md-5 px-2">
            <h6 className="text-primary fw-bold text-uppercase tracking-wider mb-2 landing-text-xs">{t('howItWorks.tagline')}</h6>
            <h2 className="fw-bold mb-3 mb-md-4 landing-title">{t('sections.howItWorks')}</h2>
            <p className="text-muted mb-0 landing-text">{t('howItWorks.description')}</p>
          </div>
          
          <Row className="align-items-center mb-4 mb-md-5">
            <Col lg={5} className="text-center mb-4 mb-lg-0">
              <img 
                src="/images/steps-illustration.svg" 
                alt={t('howItWorks.imageAlt')}
                className="img-fluid steps-illustration"
                style={{maxWidth: '400px'}}
                loading="lazy"
                decoding="async"
              />
            </Col>
            <Col lg={7}>
              <Row className="steps-section g-3 g-md-4">
                {getSafeArray('howItWorks.steps', []).map((step, index) => (
                  <Col key={index} md={12} className="mb-3 mb-md-4">
                    <div className="step-item d-flex align-items-start text-start">
                      <div className={`step-number shadow ${index === 1 ? 'bg-primary' : ''} me-2 me-md-3 flex-shrink-0`} style={{ width: '40px', height: '40px' }}>{index + 1}</div>
                      <div>
                        <h4 className="mb-2 landing-text">{step.title}</h4>
                        <p className="text-muted mb-0 landing-text-sm" style={{ lineHeight: '1.6' }}>
                          {step.description}
                        </p>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features */}
      <section className="section bg-light py-4 py-md-5" id="features" aria-label="Features">
        <Container>
          <div className="text-center mb-4 mb-md-5 px-2">
            <h2 className="fw-bold mb-3 mb-md-4 landing-title">{t('sections.features')}</h2>
            <p className="text-muted mb-0 landing-text">{t('features.description')}</p>
          </div>
          <Row className="align-items-center mb-4 mb-md-5">
            <Col lg={6} className="text-center text-lg-start mb-4 mb-lg-0">
            <div className="feature-image-container">
                <img 
                  src="/images/features-illustration.svg" 
                  alt={t('features.imageAlt')}
                  className="img-fluid features-illustration"
                  style={{maxWidth: '500px'}}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </Col>
            <Col lg={6}>
              <Row className="g-3">
                {getSafeArray('features.cards', []).map((card, index) => {
                  const icons = ['bi-camera-video-fill', 'bi-bell-fill', 'bi-geo-alt-fill', 'bi-people-fill', 'bi-qr-code-scan', 'bi-shield-check', 'bi-link-45deg'];
                  const colors = ['primary', 'success', 'warning', 'info', 'danger', 'dark', 'primary'];
                  return (
                    <Col key={index} xs={12} sm={6}>
                      <Card className="feature-card-compact border-0 shadow-sm h-100">
                        <Card.Body className="p-3">
                          <div className={`card-icon bg-${colors[index]} bg-opacity-10 p-2 rounded-circle d-inline-flex align-items-center justify-content-center mb-2`}>
                            <i className={`bi ${icons[index]} fs-5 text-${colors[index]}`}></i>
                          </div>
                          <Card.Title className="fw-bold mb-2 landing-text">{card.title}</Card.Title>
                          <Card.Text className="text-muted mb-0 landing-text-sm" style={{ lineHeight: '1.5' }}>
                            {card.description}
                          </Card.Text>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Pricing Section - Removed */}

      {/* Use Cases */}
      <section className="section bg-light py-4 py-md-5" id="use-cases" aria-label="Use cases">
        <Container>
          <div className="text-center mb-4 mb-md-5 px-2">
            <h2 className="fw-bold mb-3 mb-md-4 landing-title">{t('sections.useCases')}</h2>
            <p className="text-muted mb-0 landing-text">{t('useCases.description')}</p>
          </div>

          <Row className="align-items-center mb-4 mb-md-5">
            <Col lg={5} className="mb-4 mb-lg-0 text-center">
              <img 
                src="/images/use-case-support.svg" 
                alt={t('useCases.imageAlt')}
                className="img-fluid use-case-illustration"
                style={{maxWidth: '350px'}}
                loading="lazy"
                decoding="async"
              />
            </Col>
            <Col lg={7}>
          <Row className="g-3 g-md-4">
                {getSafeArray('useCases.categories', []).map((category, index) => {
                  const icons = ['bi-headset', 'bi-house-check', 'bi-bicycle'];
                  const colors = ['primary', 'success', 'info'];
                  const isFirst = index === 0;
                  return (
                    <Col key={index} xs={12} md={isFirst ? 12 : 6}>
                      <div className={`d-flex flex-column ${isFirst ? 'flex-md-row' : ''} p-3 p-md-4 border rounded-3 ${isFirst ? '' : 'h-100'} hover-shadow transition-all`}>
                        <div className={`${isFirst ? 'me-0 me-md-4' : ''} mb-3 ${isFirst ? 'mb-md-0 flex-shrink-0' : ''}`}>
                          <div className={`bg-${colors[index]} bg-opacity-10 p-2 p-md-3 rounded d-inline-${isFirst ? 'flex' : 'block'} align-items-center justify-content-center`}>
                            <i className={`bi ${icons[index]} fs-3 text-${colors[index]}`}></i>
                          </div>
                        </div>
                        <div>
                          <h4 className={`fw-bold mb-2 ${isFirst ? 'landing-text' : 'landing-text-md'}`}>{category.title}</h4>
                          <p className="text-muted mb-2 landing-text-sm" style={{ lineHeight: isFirst ? '1.6' : '1.5' }}>
                            {category.description}
                          </p>
                          <div className="d-flex flex-wrap gap-2">
                            {Array.isArray(category.tags) && category.tags.map((tag, tagIndex) => (
                              <Badge key={tagIndex} bg="light" text="dark" className="border landing-text-xs">{tag}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Featured guides */}
      <section className="section py-4 py-md-5" id="featured-guides" aria-label={t('featuredGuides.title')}>
        <Container>
          <div className="text-center mb-4 mb-md-5 px-2">
            <h6 className="text-primary fw-bold text-uppercase tracking-wider mb-2 landing-text-xs">{t('featuredGuides.tagline')}</h6>
            <h2 className="fw-bold mb-3 mb-md-4 landing-title">{t('featuredGuides.title')}</h2>
            <p className="text-muted mb-0 landing-text">{t('featuredGuides.description')}</p>
          </div>
          <Row className="g-3 g-md-4">
            {featuredGuides.map((guide) => (
              <Col key={guide.id} xs={12} md={6} lg={4}>
                <Card className="h-100 border-0 shadow-sm position-relative">
                  <Card.Body className="p-4 d-flex flex-column">
                    <Card.Title className="h5 fw-bold mb-3">
                      {t(`guides:articles.${guide.id}.h1`)}
                    </Card.Title>
                    <Card.Text className="text-muted landing-text-sm flex-grow-1">
                      {t(`guides:articles.${guide.id}.meta.description`)}
                    </Card.Text>
                    <Link
                      to={guide.slug}
                      className="stretched-link"
                      aria-label={t(`guides:articles.${guide.id}.h1`)}
                    />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-4">
            <Button as={Link} to="/guides" variant="outline-primary">
              {t('featuredGuides.viewAll')}
            </Button>
          </div>
        </Container>
      </section>

      {/* FAQ */}
      <section className="section py-4 py-md-5" id="faq" aria-label="Frequently asked questions">
        <Container>
          <div className="text-center mb-4 mb-md-5 px-2">
            <h6 className="text-primary fw-bold text-uppercase tracking-wider mb-2 landing-text-xs">{t('faq.tagline')}</h6>
            <h2 className="fw-bold mb-3 mb-md-4 landing-title">{t('sections.faq')}</h2>
            <p className="text-muted mb-0 landing-text">{t('faq.description')}</p>
          </div>
          
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
          <Accordion>
            <Accordion.Item eventKey="0" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-phone me-2 text-primary"></i>
                  <span>{t('faq.q1.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <div className="d-flex align-items-start mb-3">
                  <i className="bi bi-check-circle-fill text-success me-2 mt-1" style={{ fontSize: '1.1rem' }}></i>
                  <div dangerouslySetInnerHTML={{ __html: t('faq.q1.answer') }} />
                </div>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-credit-card me-2 text-primary"></i>
                  <span>{t('faq.q2.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <p className="mb-3 landing-text-md" dangerouslySetInnerHTML={{ __html: t('faq.q2.answer') }} />
                <div className="mb-3">
                  {getSafeArray('faq.q2.plans', []).map((plan, index) => {
                    const colors = ['primary', 'success', 'warning'];
                    return (
                      <div key={index} className="d-flex align-items-start mb-2">
                        <Badge bg={colors[index]} className="me-2 mt-1">{plan.name}</Badge>
                        <div className="landing-faq-text">
                          <strong dangerouslySetInnerHTML={{ __html: plan.price }} /> ou <strong dangerouslySetInnerHTML={{ __html: plan.priceYearly }} /> • {plan.features}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mb-2 landing-text-md">
                  <strong>{t('faq.q2.options.title')}</strong>
                </p>
                <ul className="mb-3 landing-faq-text">
                  {getSafeArray('faq.q2.options.items', []).map((item, index) => (
                    <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                  ))}
                </ul>
                <Alert variant="success" className="mb-0 mt-3 border-0 landing-faq-text" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
                  <i className="bi bi-gift-fill me-2"></i>
                  <strong>{t('faq.q2.promo.title')}</strong> {t('faq.q2.promo.text')} <Badge bg="success" className="mx-1">{t('faq.q2.promo.code')}</Badge>
                  <br />
                  <small className="text-muted">{t('faq.q2.promo.note')}</small>
                </Alert>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-geo-alt me-2 text-primary"></i>
                  <span>{t('faq.q3.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="landing-text-md">
                {t('faq.q3.answer').split('<Badge>').map((part, index) => {
                  if (index === 0) return part;
                  const [badgeText, rest] = part.split('</Badge>');
                  const badgeColor = badgeText === 'Business' ? 'success' : 'warning';
                  return (
                    <React.Fragment key={index}>
                      <Badge bg={badgeColor} className="ms-1 me-1">{badgeText}</Badge>
                      {rest}
                    </React.Fragment>
                  );
                })}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-list-ul me-2 text-primary"></i>
                  <span>{t('faq.q4.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="landing-text-md">
                {t('faq.q4.answer')}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="4" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-people me-2 text-primary"></i>
                  <span>{t('faq.q5.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <p className="mb-3 landing-text-md" dangerouslySetInnerHTML={{ __html: t('faq.q5.answer') }} />
                <ul className="mb-3 landing-faq-text">
                  {getSafeArray('faq.q5.capabilities', []).map((capability, index) => (
                    <li key={index}>{capability}</li>
                  ))}
                </ul>
                <p className="mb-2 landing-text-md">
                  <strong>{t('faq.q5.limits.title')}</strong>
                </p>
                <div className="mb-3">
                  {getSafeArray('faq.q5.limits.plans', []).map((plan, index) => {
                    const colors = ['primary', 'success', 'warning'];
                    return (
                      <div key={index} className="d-flex align-items-center mb-2">
                        <Badge bg={colors[index]} className="me-2" style={{ minWidth: '60px' }}>{plan.name}</Badge>
                        <span className="landing-faq-text">{plan.agents}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="mb-0 landing-text-md" dangerouslySetInnerHTML={{ __html: t('faq.q5.additional') }} />
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="5" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-qr-code me-2 text-primary"></i>
                  <span>{t('faq.q6.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <p className="mb-3 landing-text-md" dangerouslySetInnerHTML={{ __html: t('faq.q6.answer') }} />
                <p className="mb-2 landing-text-md">
                  <strong>{t('faq.q6.examples.title')}</strong>
                </p>
                <div className="row g-2 mb-3">
                  {getSafeArray('faq.q6.examples.items', []).map((example, index) => {
                    const colors = ['primary', 'success', 'warning', 'info'];
                    return (
                      <Col key={index} xs={12} sm={6}>
                        <div className="p-2 bg-light rounded landing-text-sm">
                          <i className={`bi bi-${example.icon} text-${colors[index]} me-2`}></i>
                          <strong>{example.title}</strong> {example.text}
                        </div>
                      </Col>
                    );
                  })}
                </div>
                <p className="mb-2 landing-text-md">
                  <strong>{t('faq.q6.limits.title')}</strong>
                </p>
                <div className="mb-3">
                  {getSafeArray('faq.q6.limits.plans', []).map((plan, index) => {
                    const colors = ['secondary', 'primary', 'success', 'warning'];
                    return (
                      <div key={index} className="d-flex align-items-center mb-2">
                        <Badge bg={colors[index]} className="me-2" style={{ minWidth: '60px' }}>{plan.name}</Badge>
                        <span className="landing-faq-text">{plan.limit}</span>
                      </div>
                    );
                  })}
                </div>
                <Alert variant="info" className="mb-0 mt-3 border-0 landing-faq-text" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                  <i className="bi bi-plus-circle-fill me-2"></i>
                  <strong>{t('faq.q6.additional.title')}</strong> {t('faq.q6.additional.text')}
                </Alert>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="6" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-wifi me-2 text-primary"></i>
                  <span>{t('faq.q7.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <p className="mb-3 landing-text-md" dangerouslySetInnerHTML={{ __html: t('faq.q7.answer') }} />
                <Alert variant="warning" className="mb-0 mt-3 border-0 landing-faq-text" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                  <i className="bi bi-lightning-charge-fill me-2"></i>
                  <strong>{t('faq.q7.recommendation.title')}</strong> <span dangerouslySetInnerHTML={{ __html: t('faq.q7.recommendation.text') }} />
                </Alert>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="7" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-shield-check me-2 text-primary"></i>
                  <span>{t('faq.q8.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <p className="mb-3 landing-text-md" dangerouslySetInnerHTML={{ __html: t('faq.q8.answer') }} />
                <Alert variant="info" className="mb-3 mt-3 border-0 landing-faq-text" style={{ backgroundColor: 'rgba(13, 110, 253, 0.1)' }}>
                  <i className="bi bi-cookie me-2"></i>
                  <strong>{t('faq.q8.cookies.title')}</strong> <span dangerouslySetInnerHTML={{ __html: t('faq.q8.cookies.text') }} />
                </Alert>
                <Alert variant="success" className="mb-0 mt-3 border-0 landing-faq-text" style={{ backgroundColor: 'rgba(25, 135, 84, 0.1)' }}>
                  <i className="bi bi-building me-2"></i>
                  <strong>{t('faq.q8.onPremise.title')}</strong> <span dangerouslySetInnerHTML={{ __html: t('faq.q8.onPremise.text') }} />
                </Alert>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="9" className="mb-3 border rounded shadow-sm">
              <Accordion.Header>
                <div className="d-flex align-items-center">
                  <i className="bi bi-palette me-2 text-primary"></i>
                  <span>{t('faq.q9.question')}</span>
                </div>
              </Accordion.Header>
              <Accordion.Body className="landing-text-md">
                {t('faq.q9.answer')}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section cta-section text-center py-4 py-md-5" id="demo" aria-label="Call to action">
        <Container>
          <Row className="align-items-center">
            <Col lg={7} className="text-center text-lg-start mb-4 mb-lg-0 px-3 px-md-0">
            <h2 className="fw-bold mb-3 mb-md-4 landing-title">{t('cta.modernize')}</h2>
              <p className="mb-3 mb-md-4 opacity-75 landing-text">
              {t('cta.joinCompanies')}
            </p>
              
              {/* Message gratuité renforcé */}
              <Alert variant="success" className="mb-3 mb-md-4 text-start border-0 shadow-sm">
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-shield-check-fill me-2" style={{ fontSize: '1.5rem' }}></i>
                  <h5 className="mb-0 fw-bold landing-text">{t('cta.freeAlert.title')}</h5>
                </div>
                <p className="mb-0 landing-text-sm" style={{ lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: t('cta.freeAlert.text') }} />
              </Alert>
              
              <div className="d-flex flex-column flex-sm-row justify-content-center justify-content-lg-start gap-2 gap-md-3 mb-3 mb-md-4">
              <Button variant="light" size="lg" as={Link} to="/register" className="px-4 px-md-5 py-2 py-md-3 fw-bold text-primary shadow-lg">
                <i className="bi bi-lightning-charge-fill me-2"></i>
                <span className="d-none d-sm-inline">{t('cta.testNow')} • </span>30 secondes
              </Button>
              <Button variant="outline-light" size="lg" href="mailto:support@leagora.fr" className="px-4 px-md-5 py-2 py-md-3">
                  {t('cta.contact')}
              </Button>
            </div>
            <div className="d-flex flex-column flex-sm-row flex-wrap justify-content-center justify-content-lg-start gap-2 gap-md-3 opacity-75 landing-text-sm">
              {getSafeArray('cta.features', []).map((feature, index) => {
                const icons = ['bi-gift-fill', 'bi-credit-card-2-front', 'bi-clock', 'bi-shield-check'];
                return (
                  <div key={index} className="d-flex align-items-center">
                    <i className={`bi ${icons[index]} me-2`}></i> {feature}
                  </div>
                );
              })}
            </div>
            </Col>
            <Col lg={5} className="d-none d-lg-block">
              <div className="cta-image-container text-center">
                <img 
                  src="/images/cta-illustration.svg" 
                  alt={t('cta.imageAlt')}
                  className="img-fluid cta-illustration"
                  style={{maxWidth: '300px'}}
                  loading="lazy"
                  decoding="async"
                />
          </div>
            </Col>
          </Row>
        </Container>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-dark text-white py-4 py-md-5">
        <Container>
          <Row className="g-4">
            <Col xs={12} md={3}>
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-headset fs-4 text-primary"></i>
                <span className="h5 mb-0 fw-bold">{t('brand.name')}</span>
              </div>
              <p className="text-white-50 mb-3 mb-md-4 landing-faq-text" style={{ lineHeight: '1.6' }}>
                {t('footer.tagline')}
              </p>
              <div className="d-flex gap-3 mt-3 mt-md-4">
                <a href={t('footer.social.linkedin')} target="_blank" rel="noopener noreferrer" className="text-white-50 hover-white" aria-label="LinkedIn">
                  <i className="bi bi-linkedin fs-5"></i>
                </a>
                <a href={t('footer.social.website')} target="_blank" rel="noopener noreferrer" className="text-white-50 hover-white" aria-label="Leagora">
                  <i className="bi bi-globe2 fs-5"></i>
                </a>
              </div>
            </Col>
            <Col xs={6} md={2}>
              <h6 className="fw-bold mb-3 landing-text-md">{t('footer.product')}</h6>
              <ul className="list-unstyled text-white-50">
                <li className="mb-2"><a href="#features" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.features')}</a></li>
                <li className="mb-2"><a href="#use-cases" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.useCases')}</a></li>
                <li className="mb-2"><Link to="/guides" className="text-reset text-decoration-none landing-text-sm">{t('nav.guides')}</Link></li>
                <li className="mb-2"><a href="#featured-guides" className="text-reset text-decoration-none landing-text-sm">{t('featuredGuides.footerLink')}</a></li>
              </ul>
            </Col>
            <Col xs={6} md={2}>
              <h6 className="fw-bold mb-3 landing-text-md">{t('footer.resources')}</h6>
              <ul className="list-unstyled text-white-50">
                <li className="mb-2"><a href="#faq" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.faq')}</a></li>
                <li className="mb-2"><Link to="/mentions-legales" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.legalNotice')}</Link></li>
                <li className="mb-2"><Link to="/cgv" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.cgv')}</Link></li>
                <li className="mb-2"><Link to="/cgu-b2b" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.cgu')}</Link></li>
                <li className="mb-2"><Link to="/confidentialite" className="text-reset text-decoration-none landing-text-sm">{t('common:footer.privacy')}</Link></li>
              </ul>
            </Col>
            <Col xs={6} md={2}>
              <h6 className="fw-bold mb-3 landing-text-md">{t('footer.ecosystem')}</h6>
              <ul className="list-unstyled text-white-50">
                <li className="mb-2">
                  <a
                    href="https://leagora.io/rgpd/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-reset text-decoration-none landing-text-sm"
                  >
                    {t('footer.ecosystemLinks.rgpd')}
                  </a>
                </li>
                <li className="mb-2">
                  <a
                    href="https://leagora.io/hebergement-france/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-reset text-decoration-none landing-text-sm"
                  >
                    {t('footer.ecosystemLinks.hosting')}
                  </a>
                </li>
                <li className="mb-2">
                  <a
                    href="https://leagora.io/webrtc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-reset text-decoration-none landing-text-sm"
                  >
                    {t('footer.ecosystemLinks.webrtc')}
                  </a>
                </li>
              </ul>
            </Col>
            <Col xs={12} md={3}>
              <h6 className="fw-bold mb-3 landing-text-md">{t('footer.contact')}</h6>
              <ul className="list-unstyled text-white-50">
                <li className="mb-2 landing-text-sm"><i className="bi bi-envelope me-2"></i> support@leagora.fr</li>
              </ul>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default LandingPage;
